import { useState, type ReactNode } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Stack, Typography, useTheme } from '@mui/material';
import { getSessionDrawerChrome, sessionDrawerCollapsibleSectionSx } from '@kangaroo/ui';

type TelemetryDrawerSectionProps = {
  title: string;
  description?: string;
  children: ReactNode;
  defaultExpanded?: boolean;
  collapsible?: boolean;
};

export function TelemetryDrawerSection({
  title,
  description,
  children,
  defaultExpanded = true,
  collapsible = true,
}: TelemetryDrawerSectionProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getSessionDrawerChrome(isDark);
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={sessionDrawerCollapsibleSectionSx(isDark)}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={0.5}
        onClick={collapsible ? () => setExpanded((value) => !value) : undefined}
        sx={{
          minWidth: 0,
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: '0.76rem',
              fontWeight: 850,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: expanded ? chrome.collapsibleTitle : chrome.collapsibleTitleCollapsed,
            }}
          >
            {title}
          </Typography>
          {description && expanded ? (
            <Typography sx={{ mt: 0.35, fontSize: '0.74rem', color: chrome.body, lineHeight: 1.45 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        {collapsible ? (
          <ExpandMoreIcon
            aria-hidden
            sx={{
              fontSize: 22,
              mt: -0.1,
              flexShrink: 0,
              color: chrome.collapsibleIcon,
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 160ms ease',
            }}
          />
        ) : null}
      </Stack>
      <Collapse in={!collapsible || expanded} unmountOnExit={false}>
        <Box sx={{ mt: 1.1 }} onClick={(event) => event.stopPropagation()}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
